/*
 This file is part of GNU Taler
 (C) 2022-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/
import {
  ChallengerApi,
  HttpStatusCode,
  TalerError,
} from "@gnu-taler/taler-util";
import { useTranslationContext } from "@gnu-taler/web-util/browser";
import { ComponentChildren, VNode, h } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { useChallengeSession } from "../hooks/challenge.js";
import { SessionId } from "../hooks/session.js";
import {
  ActionButton,
  formatErrorDetails,
  formatHttpErrorDetails,
  InlineNotice,
  LoadingState,
  SecondaryAction,
  TechnicalDetails,
  VerificationCard,
} from "./VerificationUi.js";

export type ChallengeNextPage = "ask" | "answer" | "completed" | "exhausted";

export function getChallengeNextPage(
  status: ChallengerApi.ChallengeStatus,
): ChallengeNextPage {
  if (status.solved) return "completed";
  if (status.last_address && status.auth_attempts_left > 0) return "answer";
  if (status.pin_transmissions_left > 0 || status.changes_left > 0)
    return "ask";
  return "exhausted";
}

interface Props {
  session: SessionId;
  children: ComponentChildren;
  onStatus?: (nextPage: ChallengeNextPage) => void;
}

export function CheckChallengeIsUpToDate({
  session,
  children,
  onStatus,
}: Props): VNode {
  const { i18n } = useTranslationContext();
  const { result, retry } = useChallengeSession(session);
  const returnHost = new URL(session.redirectURL).host;
  // Translators: %1$s is the host name of the application that requested
  // verification.
  const returnLabel = i18n.str`Return to ${returnHost}`;
  // Translators: The user has exhausted the verification attempts allowed for
  // this session, not network retry attempts.
  const noAttemptsTitle = i18n.str`No attempts remain`;
  // Translators: The user has exhausted all tries to enter a verification
  // code, rather than connection retry attempts.
  const noVerificationAttemptsTitle = i18n.str`No verification attempts remain`;
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  const nextPage =
    result && !(result instanceof TalerError) && result.type === "ok"
      ? getChallengeNextPage(result.body)
      : undefined;

  useEffect(() => {
    if (nextPage && nextPage !== "exhausted") {
      onStatusRef.current?.(nextPage);
    }
  }, [nextPage]);

  if (!result) {
    // Translators: Initial loading screen while the existing verification
    // session is fetched from Challenger.
    const loadingTitle = i18n.str`Loading verification…`;
    // Translators: Reassurance below the initial verification loading title.
    const loadingDescription = i18n.str`This can take a few seconds.`;
    return (
      <LoadingState title={loadingTitle} description={loadingDescription} />
    );
  }
  if (result instanceof TalerError) {
    // Translators: Page title shown when the browser cannot contact Challenger
    // to retrieve an existing verification session.
    const unavailableTitle = i18n.str`Verification service unavailable`;
    // Translators: Error-notice heading for a failed attempt to retrieve the
    // current verification session.
    const loadFailureTitle = i18n.str`We could not load this verification`;
    // Translators: Reassures the user that a connection failure did not discard
    // the verification steps they already completed.
    const loadFailureDescription = i18n.str`Check your connection and try again. Your progress is safe.`;
    // Translators: Busy-state label while retrying a verification request.
    const loadingLabel = i18n.str`Loading…`;
    // Translators: Button that retries a failed request to Challenger.
    const retryLabel = i18n.str`Try again`;
    const technicalDetails = formatErrorDetails(result);
    return (
      <VerificationCard title={unavailableTitle}>
        <InlineNotice tone="error" title={loadFailureTitle}>
          {loadFailureDescription}
        </InlineNotice>
        <div class="mt-5">
          <ActionButton
            busyLabel={loadingLabel}
            onClick={async () => {
              await retry();
            }}
          >
            {retryLabel}
          </ActionButton>
        </div>
        <TechnicalDetails copyText={technicalDetails}>
          {technicalDetails}
        </TechnicalDetails>
      </VerificationCard>
    );
  }

  if (result.type === "fail") {
    const retryable = result.case === HttpStatusCode.InternalServerError;
    // Translators: Error heading for a verification session that has expired or
    // was already removed by Challenger.
    const expiredTitle = i18n.str`Verification link expired`;
    // Translators: Error heading for a malformed or otherwise unusable
    // verification-session link.
    const invalidTitle = i18n.str`Invalid verification link`;
    // Translators: Explanation for an expired or removed verification session.
    const unavailableMessage = i18n.str`This verification is no longer available. Return to the application and start again.`;
    // Translators: Instruction shown after the user has exhausted all allowed
    // verification attempts.
    const exhaustedMessage = i18n.str`Return to the application and start a new verification.`;
    // Translators: Retry instruction for a temporary server-side failure.
    const temporaryFailureMessage = i18n.str`The service had a temporary problem. Try again.`;
    // Translators: Explanation for a verification request that Challenger
    // rejected because required data is absent or invalid.
    const invalidRequestMessage = i18n.str`The verification request is incomplete or invalid.`;
    const title =
      result.case === HttpStatusCode.NotFound
        ? expiredTitle
        : result.case === HttpStatusCode.TooManyRequests
          ? noAttemptsTitle
          : result.case === HttpStatusCode.InternalServerError
            ? i18n.str`Verification service unavailable`
            : invalidTitle;
    const message =
      result.case === HttpStatusCode.NotFound
        ? unavailableMessage
        : result.case === HttpStatusCode.TooManyRequests
          ? exhaustedMessage
          : result.case === HttpStatusCode.InternalServerError
            ? temporaryFailureMessage
            : invalidRequestMessage;
    const technicalDetails = formatHttpErrorDetails(result);
    return (
      <VerificationCard title={title}>
        <InlineNotice tone="error" title={title}>
          {message}
        </InlineNotice>
        <div class="mt-5 space-y-3">
          {retryable ? (
            <ActionButton
              busyLabel={i18n.str`Loading…`}
              onClick={async () => {
                await retry();
              }}
            >
              <i18n.Translate>Try again</i18n.Translate>
            </ActionButton>
          ) : undefined}
          <SecondaryAction href={session.redirectURL}>
            {returnLabel}
          </SecondaryAction>
        </div>
        <TechnicalDetails copyText={technicalDetails}>
          {technicalDetails}
        </TechnicalDetails>
      </VerificationCard>
    );
  }

  if (nextPage && nextPage !== "exhausted") {
    // Translators: Brief transition screen while routing to the next step of
    // the verification flow.
    const continuingTitle = i18n.str`Continuing…`;
    return <LoadingState title={continuingTitle} />;
  }

  return (
    <VerificationCard
      step="code"
      title={noVerificationAttemptsTitle}
      description={children}
    >
      <SecondaryAction href={session.redirectURL}>
        {returnLabel}
      </SecondaryAction>
    </VerificationCard>
  );
}
